import { z } from 'zod';
import { isoDateTime, platformSchema, regionSchema, supportStateSchema, uuid, } from './primitives.js';
/**
 * Franchise ist reine Gruppierungs-/Metadatenebene.
 * Kein Game, kein Trophy Set, nicht anklickbar.
 */
export const franchiseSchema = z.object({
    id: uuid,
    name: z.string().min(1),
    slug: z.string().min(1),
    popularityRank: z.number().int().nullable(),
    igdbId: z.number().int().nullable(),
});
/**
 * Game = uebergeordnete Spieleidentitaet. Metadaten aus IGDB.
 * Traegt selbst KEINE Trophy-Daten.
 */
export const gameSchema = z.object({
    id: uuid,
    title: z.string().min(1),
    slug: z.string().min(1),
    franchiseId: uuid.nullable(),
    coverUrl: z.string().url().nullable(),
    releaseDate: z.string().nullable(),
    publisher: z.string().nullable(),
    developer: z.string().nullable(),
    platforms: z.array(platformSchema),
    supportState: supportStateSchema,
    igdbId: z.number().int().nullable(),
});
/**
 * TrophySet ist plattform-, region- und versionsbezogen und gehoert zu genau
 * einem Game. Fortschritt, Completion und Platin haengen IMMER am Set,
 * niemals am Game. PS4 und PS5 werden nie zusammengefuehrt.
 */
export const trophySetSchema = z.object({
    id: uuid,
    gameId: uuid,
    npCommunicationId: z.string().min(1),
    platform: platformSchema,
    region: regionSchema,
    version: z.string().nullable(),
    title: z.string().min(1),
    trophyTitleIconUrl: z.string().url().nullable(),
    trophyCount: z.object({
        bronze: z.number().int().nonnegative(),
        silver: z.number().int().nonnegative(),
        gold: z.number().int().nonnegative(),
        platinum: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
    hasPlatinum: z.boolean(),
    supportState: supportStateSchema,
    psnLastSyncedAt: isoDateTime.nullable(),
});
export const trophyGroupKindSchema = z.enum(['base', 'dlc']);
/**
 * Eine Trophy Group ist entweder Base Game oder eine DLC-/Zusatzgruppe.
 * Platin gehoert immer zur Base-Group. DLC-Completion bleibt separat.
 */
export const trophyGroupSchema = z.object({
    id: uuid,
    trophySetId: uuid,
    psnGroupId: z.string().min(1),
    kind: trophyGroupKindSchema,
    name: z.string().min(1),
    detail: z.string().nullable(),
    iconUrl: z.string().url().nullable(),
    order: z.number().int().nonnegative(),
    trophyCount: z.number().int().nonnegative(),
});
export const trophyTypeSchema = z.enum(['bronze', 'silver', 'gold', 'platinum']);
/** Nur PSN-Daten. Wird ausschliesslich vom PSN-Sync geschrieben. */
export const trophyPsnDataSchema = z.object({
    psnTrophyId: z.number().int().nonnegative(),
    name: z.string(),
    detail: z.string().nullable(),
    type: trophyTypeSchema,
    iconUrl: z.string().url().nullable(),
    hidden: z.boolean(),
});
/**
 * Nur PlatHunt-Redaktionsdaten. Wird ausschliesslich vom Admin geschrieben.
 * Ein PSN-Sync darf diese Felder niemals anfassen.
 */
export const trophyEditorialSchema = z.object({
    tip: z.string().nullable(),
    missable: z.boolean(),
    pointOfNoReturn: z.boolean(),
    customDisplayLogoUrl: z.string().url().nullable(),
});
export const trophySchema = z.object({
    id: uuid,
    trophySetId: uuid,
    trophyGroupId: uuid,
    order: z.number().int().nonnegative(),
    psn: trophyPsnDataSchema,
    editorial: trophyEditorialSchema,
});
/**
 * Anzeigeregel (final):
 * customDisplayLogoUrl ?? trophyGroupIconUrl ?? trophyTitleIconUrl
 */
export function resolveDisplayAsset(input) {
    return (input.customDisplayLogoUrl ?? input.trophyGroupIconUrl ?? input.trophyTitleIconUrl ?? null);
}
/** Welche Game-Hub-Tabs der Support State freischaltet. */
export const gameHubTabsBySupportState = {
    full: ['overview', 'trophies', 'roadmap', 'completion', 'more'],
    basic: ['overview', 'trophies', 'more'],
    catalog: [],
};
//# sourceMappingURL=catalog.js.map